// src/components/chat/hooks/useChat.js
import { useState, useEffect } from 'react';
import axios from 'axios';

export const useChat = () => {
    const [messages, setMessages] = useState({
        daily: [],
        event: [],
        combine: []
    });
    const [profileData, setProfileData] = useState(null);
    const [email, setEmail] = useState('');
    const [stage, setStage] = useState(0); // Этапы регистрации
    const [userLooks, setUserLooks] = useState([]);
    const [isButtonVisible, setIsButtonVisible] = useState(false);
    const [activeSection, setActiveSection] = useState('daily');
    const [visitedSections, setVisitedSections] = useState({
        daily: false,
        event: false,
        combine: false
    });
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    // Лимит сообщений
    const MAX_STORED_MESSAGES = 2;
    const MAX_STORED_MESSAGES_EVENT = 1;

    // Функция для ограничения количества сообщений
    const limitMessagesEvent = (messageArray) => {
        if (!Array.isArray(messageArray)) return [];
        return messageArray.slice(-MAX_STORED_MESSAGES_EVENT);
    };

    const limitMessages = (messageArray) => {
        if (!Array.isArray(messageArray)) return [];
        return messageArray.slice(-MAX_STORED_MESSAGES);
    };

    // Функция добавления сообщения для определенного раздела
    const addMessage = (text, sender, section = 'daily') => {
        if (!text?.trim()) {
            console.error('Empty message received, not adding to chat.');
            return;
        }
        
        setMessages(prevMessages => {
            const sectionMessages = Array.isArray(prevMessages[section]) 
                ? prevMessages[section] 
                : [];
                
            const newSectionMessages = limitMessages([
                ...sectionMessages, 
                { text, sender }
            ]);
            
            return {
                ...prevMessages,
                [section]: newSectionMessages
            };
        });
    };

    const saveMessagesToCookies = (messages) => {
        // Ограничиваем сообщения перед сохранением
        const limitedMessages = {
            daily: limitMessages(messages.daily),
            event: limitMessagesEvent(messages.event),
            combine: limitMessages(messages.combine)
        };
        
        const expirationDate = new Date();
        expirationDate.setTime(expirationDate.getTime() + (7 * 24 * 60 * 60 * 1000));
        document.cookie = `chatMessages=${JSON.stringify(limitedMessages)}; path=/; expires=${expirationDate.toUTCString()}`;
    };

    const getCookie = (name) => {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) return parts.pop().split(';').shift();
        return '';
    };

    const saveEmailToCookies = (email) => {
        if (email) {
            const expirationDate = new Date();
            expirationDate.setTime(expirationDate.getTime() + (30 * 24 * 60 * 60 * 1000)); // 30 дней
            document.cookie = `user_email=${email}; path=/; expires=${expirationDate.toUTCString()}`;
            console.log('Email saved to cookies:', email);
        } else {
            console.error('Email is empty, not saving to cookies.');
        }
    };

    const fetchUserData = async (userEmail) => {
        try {
            setIsLoading(true);
            
            // Получаем данные профиля
            const profileResponse = await axios.get('/api/profile', {
                params: { email: userEmail }
            });
            
            if (profileResponse.data) {
                console.log('Profile data fetched:', profileResponse.data);
                setProfileData(profileResponse.data);
                setIsLoggedIn(true);
                setStage(5); // Устанавливаем этап на 5 (завершенная регистрация)
                
                // Получаем созданные образы
                try {
                    const looksResponse = await axios.get('/getUserLooks', { 
                        params: { email: userEmail } 
                    });
                    
                    if (looksResponse.data) {
                        setUserLooks(looksResponse.data);
                    }
                } catch (looksError) {
                    console.error('Error fetching user looks:', looksError);
                    // Если образов нет, просто продолжаем работу
                }
                
                // Проверяем количество созданных сегодня образов
                try {
                    const today = new Date().toISOString().slice(0, 10);
                    const todaysLooksResponse = await axios.get('/getTodaysLooksCount', {
                        params: { email: userEmail, today }
                    });
                    
                    if (todaysLooksResponse.data) {
                        const todaysLooksCount = parseInt(todaysLooksResponse.data.count);
                        setIsButtonVisible(todaysLooksCount < 20);
                        
                        if (todaysLooksCount >= 20) {
                            addMessage('You have reached your daily limit of 20 outfits. Please come back tomorrow for more!', 'bot');
                        }
                    } else {
                        // Если нет данных, разрешаем создание образов
                        setIsButtonVisible(true);
                    }
                } catch (countError) {
                    console.error('Error fetching today\'s looks count:', countError);
                    // По умолчанию позволяем создавать образы
                    setIsButtonVisible(true);
                }
                
                return true;
            }
        } catch (error) {
            console.error('Error fetching user data:', error);
            return false;
        } finally {
            setIsLoading(false);
        }
        
        return false;
    };

    // Инициализация при загрузке компонента
    useEffect(() => {
        const init = async () => {
            // Получаем сохраненные сообщения и ограничиваем их количество
            const storedMessages = JSON.parse(getCookie('chatMessages') || '{}');
            const limitedStoredMessages = {
                daily: limitMessages(storedMessages.daily || []),
                event: limitMessages(storedMessages.event || []),
                combine: limitMessages(storedMessages.combine || [])
            };
            
            setMessages(limitedStoredMessages);
            
            // Проверяем, есть ли сохраненный email
            const userEmailFromCookies = getCookie('user_email');
            
            if (userEmailFromCookies) {
                setEmail(userEmailFromCookies);
                const success = await fetchUserData(userEmailFromCookies);
                
                if (!success) {
                    // Если не удалось получить данные пользователя, сбрасываем куки
                    document.cookie = 'user_email=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
                    setEmail('');
                    setIsLoggedIn(false);
                    setStage(0);
                    
                    // Добавляем приветственное сообщение
                    if (!limitedStoredMessages.daily?.length) {
                        addMessage("👋 Hey there! Please enter your email to get started.", 'bot');
                    }
                }
            } else {
                // Нет сохраненного email, показываем приветственное сообщение
                if (!limitedStoredMessages.daily?.length) {
                    addMessage("👋 Hey there! Please enter your email to get started.", 'bot');
                }
            }
        };
        
        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Сохраняем данные при их изменении
    useEffect(() => {
        if (email) {
            saveEmailToCookies(email);
        }
        
        saveMessagesToCookies(messages);
        
        if (profileData) {
            localStorage.setItem('profileData', JSON.stringify(profileData));
        }
        
        if (userLooks?.length) {
            localStorage.setItem('userLooks', JSON.stringify(userLooks));
        }
    }, [messages, profileData, userLooks, email]);

    // Обработчик переключения между разделами
    const handleSectionChange = (section) => {
        setActiveSection(section);
        
        // Инициализация раздела при первом посещении
        if (!visitedSections[section]) {
            if (section === 'event') {
                addMessage("🎉 Welcome to the Event Outfit section! Describe your event, and I'll suggest an outfit!", 'bot', 'event');
            } else if (section === 'combine') {
                addMessage("👗 Welcome to 'Combine with Mine'! Upload your item, and I'll suggest what to wear with it!", 'bot', 'combine');
            }
            
            setVisitedSections(prevState => ({
                ...prevState,
                [section]: true
            }));
        }
    };

    return {
        messages,
        setMessages,
        profileData,
        setProfileData,
        email,
        setEmail,
        stage,
        setStage,
        userLooks,
        setUserLooks,
        isButtonVisible,
        setIsButtonVisible,
        activeSection,
        handleSectionChange,
        addMessage,
        isLoggedIn,
        setIsLoggedIn,
        isLoading
    };
};